import { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
} from 'reactstrap';

/**
 * Modal component for confirming Suite Inspection Checklist deletion
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onDelete - Async callback to handle inspection deletion
 * @param {Object} props.inspection - Suite Inspection Checklist object to be deleted
 * @returns {JSX.Element} Modal for confirming suite inspection deletion
 */
const DeleteSuiteInspectionConfirmationModal = ({
    open,
    onClose,
    onDelete,
    inspection,
}) => {
    const [loading, setLoading] = useState(false);
    const handleConfirm = async () => {
        try {
            setLoading(true);
            if (onDelete) {
                await onDelete();
            }
            onClose();
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setLoading(false);
        }
    };
    const inspectionDate = inspection?.inspection_date
        ? new Date(inspection.inspection_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
        : 'this inspection';

    const buildingName = inspection?.property_details?.building_name || 'unknown property';
    const unitNumber = inspection?.unit_number || 'unknown unit';
    const suiteType = inspection?.type ? inspection.type.charAt(0).toUpperCase() + inspection.type.slice(1) : 'suite';

    return (
        <Modal isOpen={open} toggle={onClose} centered className="suite-inspection-dialog">
            <ModalHeader toggle={onClose}>Confirm Deletion</ModalHeader>
            <ModalBody>
                <p className="mb-0">
                    Are you sure you want to delete the {suiteType} Inspection for unit{' '}
                    <strong>{unitNumber}</strong> at <strong>{buildingName}</strong> on{' '}
                    <strong>{inspectionDate}</strong>?
                    <br />
                    <small className="text-muted">
                        This action cannot be undone.
                    </small>
                </p>
            </ModalBody>
            <ModalFooter>
                <Button
                    outline
                    color="secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    color="danger"
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    {loading && <Spinner size="sm" className="me-2" />}
                    {loading ? 'Deleting...' : 'Delete Inspection'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export { DeleteSuiteInspectionConfirmationModal };